import type { PropertyListing } from '@/data/listings';

export const PROPERTY_CATEGORY_LABELS: Record<PropertyListing['category'], string> = {
  daire: 'Daire', villa: 'Villa', arsa: 'Arsa', isyeri: 'İş yeri', dukkan: 'Dükkan', ofis: 'Ofis',
};

export const RESIDENTIAL_CATEGORIES: ReadonlyArray<PropertyListing['category']> = ['daire', 'villa'];
export const isLand = (category: PropertyListing['category']) => category === 'arsa';
export const isCommercial = (category: PropertyListing['category']) => category === 'isyeri' || category === 'dukkan' || category === 'ofis';

export function slugify(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u')
    .replace(/ş/g, 's').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Generates a factual summary using only the information entered for the property. */
export function generateListingSummary(listing: PropertyListing): string {
  const location = [listing.mahalle ? `${listing.mahalle} Mahallesi` : '', listing.ilce, 'Adana'].filter(Boolean).join(', ');
  const area = listing.netArea > 0 ? `${listing.netArea.toLocaleString('tr-TR')} m² net` : '';
  const grossArea = listing.grossArea > 0 ? `${listing.grossArea.toLocaleString('tr-TR')} m² brüt` : '';
  const rooms = listing.roomCount ? (isCommercial(listing.category) ? `${listing.roomCount} bölüm` : `${listing.roomCount} oda düzeni`) : '';
  const price = listing.price > 0 ? `${listing.price.toLocaleString('tr-TR')} TL${listing.type === 'kiralik' ? ' / ay' : ''}` : '';
  const land = [listing.zoning, listing.deedStatus ? `${listing.deedStatus} tapu` : '', listing.parcelInfo ? `Ada/Parsel ${listing.parcelInfo}` : ''].filter(Boolean).join(', ');
  const facts = [rooms, area, grossArea, land, price].filter(Boolean).join(' · ');
  const features = listing.features.slice(0, 3).join(', ');
  return `${location} konumunda ${listing.type === 'satilik' ? 'satılık' : 'kiralık'} ${PROPERTY_CATEGORY_LABELS[listing.category].toLocaleLowerCase('tr-TR')}.${facts ? ` ${facts}.` : ''}${features ? ` Özellikler: ${features}.` : ''}`;
}

export function generateListingSeo(listing: PropertyListing): { title: string; description: string } {
  const location = [listing.ilce, listing.mahalle].filter(Boolean).join(' ');
  const subject = `${listing.roomCount ? `${listing.roomCount} ` : ''}${listing.type === 'satilik' ? 'Satılık' : 'Kiralık'} ${PROPERTY_CATEGORY_LABELS[listing.category]}`;
  const titleBase = `Adana ${location} ${subject}`.replace(/\s+/g, ' ').trim();
  const title = titleBase.length <= 47 ? `${titleBase} | Corner Gayrimenkul` : titleBase.slice(0, 70).trim();
  const summary = generateListingSummary(listing);
  const description = summary.length > 160 ? `${summary.slice(0, 157).replace(/\s+\S*$/, '').replace(/[.,;:]$/, '')}…` : summary;
  return { title, description };
}

export function getListingQuality(listing: PropertyListing): { score: number; checks: Array<{ label: string; passed: boolean }> } {
  const checks = [
    { label: 'Açıklayıcı ilan başlığı', passed: listing.title.trim().length >= 20 },
    { label: 'En az 3 mülk fotoğrafı', passed: listing.images.filter(Boolean).length >= 3 },
    { label: 'Ayrıntılı ilan açıklaması', passed: listing.fullDescription.join(' ').trim().length >= 150 },
    { label: 'İlçe ve mahalle bilgisi', passed: Boolean(listing.ilceSlug && listing.mahalle.trim()) },
    { label: 'Adres bilgisi', passed: listing.address.trim().length >= 10 },
    { label: 'Fiyat ve alan bilgisi', passed: listing.price > 0 && listing.netArea > 0 && listing.grossArea >= listing.netArea },
    { label: 'En az 3 mülk özelliği', passed: listing.features.length >= 3 },
    { label: 'Arama motoru başlığı', passed: Boolean(listing.seoTitle && listing.seoTitle.length >= 20 && listing.seoTitle.length <= 70) },
    { label: 'Arama motoru açıklaması', passed: Boolean(listing.seoDescription && listing.seoDescription.length >= 70 && listing.seoDescription.length <= 180) },
    { label: 'Okunabilir ilan bağlantısı', passed: Boolean(listing.slug && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(listing.slug)) },
  ];
  return { score: checks.filter(check => check.passed).length * 10, checks };
}
